class DiscoverySetPopup extends HTMLElement {
  connectedCallback() {
    if (this.hasShownThisSession() || this.hasCartAddedMidWait()) return;

    const { undecidedSec, noPdpSec, mobileSec, exitIntent, pageType, pageAudience } = this.dataset;

    this.baseSeconds = {
      undecidedSec: Number(undecidedSec),
      noPdpSec: Number(noPdpSec),
      mobileSec: Number(mobileSec),
    };
    this.pageAudience = pageAudience || '';
    this.delayMultiplier = this.getDelayMultiplier();
    this.timers = [];
    this.listeners = [];
    this.shown = false;
    this.baselineItemCount = this.getBaselineItemCount();

    if (pageType === 'product') {
      sessionStorage.setItem('discoverySetPopupPdpVisited', 'true');
    }

    if (!sessionStorage.getItem('discoverySetPopupBrowseStart')) {
      sessionStorage.setItem('discoverySetPopupBrowseStart', String(Date.now()));
    }

    const isDesktop = window.matchMedia('(min-width: 768px)').matches;

    if (isDesktop) {
      this.armResettableTimer(this.getEffectiveDelayMs(this.baseSeconds.undecidedSec), ['scroll', 'click', 'pointerdown']);
      this.armNoPdpTimer(this.getEffectiveDelayMs(this.baseSeconds.noPdpSec));

      if (exitIntent === 'true' && pageType === 'product') {
        this.armExitIntent();
      }
    } else {
      this.armResettableTimer(this.getEffectiveDelayMs(this.baseSeconds.mobileSec), ['scroll', 'touchstart']);
    }

    this.bindDismissHandlers();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  hasShownThisSession() {
    return sessionStorage.getItem('discoverySetPopupShownThisSession') === 'true';
  }

  hasCartAddedMidWait() {
    return sessionStorage.getItem('discoverySetPopupCartAddedMidWait') === 'true';
  }

  getDelayMultiplier() {
    const stored = Number(localStorage.getItem('discoverySetPopupDelayMultiplier'));
    return stored > 0 ? stored : 1;
  }

  getEffectiveDelayMs(baseSeconds) {
    const effectiveSeconds = Math.min(baseSeconds * this.delayMultiplier, 300);
    return effectiveSeconds * 1000;
  }

  addListener(target, type, handler) {
    target.addEventListener(type, handler);
    this.listeners.push({ target, type, handler });
  }

  armResettableTimer(delay, resetEvents) {
    let timeoutId;

    const arm = () => {
      timeoutId = setTimeout(() => this.attemptShow(), delay);
      this.timers.push(timeoutId);
    };

    const reset = () => {
      clearTimeout(timeoutId);
      arm();
    };

    arm();
    resetEvents.forEach((type) => this.addListener(document, type, reset));
  }

  armNoPdpTimer(delay) {
    if (sessionStorage.getItem('discoverySetPopupPdpVisited') === 'true') return;

    const browseStart = Number(sessionStorage.getItem('discoverySetPopupBrowseStart'));
    const remaining = delay - (Date.now() - browseStart);

    if (remaining <= 0) {
      this.attemptShow();
      return;
    }

    this.timers.push(setTimeout(() => this.attemptShow(), remaining));
  }

  armExitIntent() {
    const handler = (event) => {
      if (event.clientY <= 0 && event.relatedTarget === null) {
        this.attemptShow();
      }
    };

    this.addListener(document, 'mouseout', handler);
  }

  bindDismissHandlers() {
    const close = this.querySelector('.discovery-set-popup__close');
    const overlay = this.querySelector('.discovery-set-popup__overlay');

    close?.addEventListener('click', () => this.hide());
    overlay?.addEventListener('click', () => this.hide());

    this.addEventListener('click', (event) => {
      const atc = event.target.closest('.cartx-add-to-cart');
      if (atc && !atc.classList.contains('cartx-sold-out')) this.hide();
    });
  }

  async attemptShow() {
    if (this.shown || this.hasShownThisSession() || this.hasCartAddedMidWait()) return;

    const [baselineItemCount, currentCart] = await Promise.all([this.baselineItemCount, this.fetchCart()]);

    if (currentCart.item_count > baselineItemCount) {
      sessionStorage.setItem('discoverySetPopupCartAddedMidWait', 'true');
      this.cleanup();
      return;
    }

    this.updateCartBaseline(currentCart.item_count);

    const cartHandles = new Set(currentCart.items.map((item) => item.handle));
    const visibleCount = this.pickProductsToShow(cartHandles);
    if (visibleCount > 0) this.showPopup();
  }

  getBaselineItemCount() {
    const stored = sessionStorage.getItem('discoverySetPopupCartBaseline');
    if (stored !== null) return Promise.resolve(Number(stored));

    return this.fetchCart().then((cart) => {
      sessionStorage.setItem('discoverySetPopupCartBaseline', String(cart.item_count));
      return cart.item_count;
    });
  }

  updateCartBaseline(itemCount) {
    sessionStorage.setItem('discoverySetPopupCartBaseline', String(itemCount));
    this.baselineItemCount = Promise.resolve(itemCount);
  }

  fetchCart() {
    return fetch('/cart.js', { cache: 'no-store' }).then((response) => response.json());
  }

  pickProductsToShow(cartHandles) {
    const allCards = [...this.querySelectorAll('.discovery-set-popup__product')];
    const audienceCards = this.pageAudience
      ? allCards.filter((card) => card.dataset.audience === this.pageAudience)
      : allCards;
    const available = audienceCards.filter((card) => !cartHandles.has(card.dataset.handle));
    const selected = available.slice(0, 2);

    allCards.forEach((card) => card.classList.add('discovery-set-popup__product--hidden'));
    selected.forEach((card) => card.classList.remove('discovery-set-popup__product--hidden'));

    return selected.length;
  }

  showPopup() {
    if (this.shown) return;

    this.shown = true;
    this.cleanup();
    this.classList.add('is-visible');
    this.lockPageScroll();
    sessionStorage.setItem('discoverySetPopupShownThisSession', 'true');
    this.escalateDelayMultiplier();
  }

  lockPageScroll() {
    const panel = this.querySelector('.discovery-set-popup__panel');
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: panel }));
  }

  unlockPageScroll() {
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
  }

  escalateDelayMultiplier() {
    const { undecidedSec, noPdpSec, mobileSec } = this.baseSeconds;
    const smallestBase = Math.min(undecidedSec, noPdpSec, mobileSec);

    if (smallestBase * this.delayMultiplier < 300) {
      localStorage.setItem('discoverySetPopupDelayMultiplier', String(this.delayMultiplier * 1.5));
    }
  }

  hide() {
    if (!this.classList.contains('is-visible')) return;

    this.classList.remove('is-visible');
    this.unlockPageScroll();
  }

  cleanup() {
    this.timers.forEach((id) => clearTimeout(id));
    this.timers = [];
    this.listeners.forEach(({ target, type, handler }) => target.removeEventListener(type, handler));
    this.listeners = [];
  }
}

if (!customElements.get('discovery-set-popup')) {
  customElements.define('discovery-set-popup', DiscoverySetPopup);
}
